import { normalizeUrlWithHttps } from "@markit/common.utils";
import { NavigationId } from "../navigation/AppParamList";
import { getUrlParamsFromString } from "../redux/sagas/initializationSaga";
import { accountActions } from "../redux/slices/accountSlice";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

// Scroll to top when page render changes
export const scrollToTop = () => {
  setTimeout(() => {
    const canControlScrollRestoration = "scrollRestoration" in window.history;
    if (canControlScrollRestoration) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, 100);
};

// Helper to navigate to the campaign dashboard
export const onCampaignNavigatePath = (campaignId: string) => {
  return `/c/${campaignId}`;
};

// Helper to navigate to the DMAutomation dashboard
export const onDMAutomationNavigatePath = (
  navigate: any,
  dmAutomationId: string
) => {
  navigate(`/dm/${dmAutomationId}`, {
    state: { dmAutomationId: dmAutomationId },
  });
};

// Helper to navigate to the event/product dashboard
export const onProductNavigatePath = (navigate: any, eventId: string) => {
  navigate(`/e/${eventId}/dashboard`, {
    state: { eventId: eventId },
  });
};

// Helper to navigate to the audience list dashboard
export const onAudienceListNavigatePath = (listId: string) => {
  return `/a/${listId}`;
};

// Helper to navigate to proper tab index on the sepcified creator panel tab
export const onCreatorPanelNavigatePath = (
  navigate: any,
  navigationTabId: NavigationId,
  initialTabValue?: number
) => {
  navigate(navigationTabId, { state: { initialTabValue } });
};

// Helper to navigate to the profile page (and immediately to subscribe flow if specified)
export const onProfilePageNavigatePath = (
  navigate: any,
  username: string,
  subscribeFlow?: "subscribe" | "manage" | "memberships",
  replace?: boolean
) => {
  navigate(
    `/u/${username}${
      subscribeFlow === "subscribe"
        ? "/subscribe"
        : subscribeFlow === "manage"
          ? "/manageSubscription"
          : subscribeFlow === "memberships"
            ? "/memberships"
            : ""
    }`,
    {
      state: { username },
      replace: replace,
    }
  );
};

// Helps handle redirect path and clears it after navigation
export const onNavigateRedirectPath = (
  navigate: any,
  dispatch: Dispatch<AnyAction>,
  redirectPath: string
) => {
  navigate(redirectPath);
  dispatch(accountActions.setRedirectPath(""));
};

// Helps handle redirect path and re-sets url params, can can replace navigation path
export const onNavigateRedirectPathWithParams = (
  navigate: any,
  dispatch: Dispatch<AnyAction>,
  redirectPath: string,
  replace?: boolean
) => {
  const params = getUrlParamsFromString(redirectPath);
  navigate(redirectPath, {
    replace: replace,
    state: {
      eventId: params.e,
      username: params.u,
      ticketId: params.ticketId,
      qrCodeId: params.qrCodeId,
      userId: params.i,
    },
  });
  dispatch(accountActions.setRedirectPath(""));
};

// Builds the redirect url for a showclix event with the linkId (maintains any query/hash in the url)
export const buildShowclixRedirectUrl = (
  rawExternalLink: string,
  linkId?: string
) => {
  const normalized = normalizeUrlWithHttps(rawExternalLink);
  if (!linkId) return normalized;

  try {
    const url = new URL(normalized);

    // Ensure single slash behavior and preserve existing query/hash
    const cleanPath = url.pathname.replace(/\/+$/, "");
    url.pathname = `${cleanPath}/tag/${encodeURIComponent(linkId)}`;

    return url.toString();
  } catch {
    // Safe fallback if URL parsing fails
    const base = normalized.replace(/\/+$/, "");
    return `${base}/tag/${encodeURIComponent(linkId)}`;
  }
};

export const isAndroidBrowser = () => {
  return /Android/i.test(navigator.userAgent);
};
